import { useState } from "react";
import { Shield, TrendingUp, BarChart3, Users, CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DemoModal from "@/components/DemoModal";
import AuthModal from "@/components/AuthModal";

const solutions = {
  "academy-owners": {
    title: "For Academy Owners",
    subtitle: "Run a smarter, more profitable academy",
    desc: "Get full visibility across all your teams, coaches, and players. Track performance academy-wide, manage billing, and demonstrate value to parents with professional reporting.",
    icon: Shield,
    benefits: [
      "Full academy performance dashboard",
      "Multi-coach management with role permissions",
      "Parent billing and subscription management",
      "Academy-wide attendance and progress reports",
      "Benchmark your academy against industry averages",
      "Data export for board meetings and investor decks",
    ],
    testimonial: {
      quote:
        "For the first time, I have complete visibility over what's happening across all 8 teams at our academy. NextTackle pays for itself in the time I save every week.",
      name: "James Richardson",
      role: "Academy Director, Pro Football Academy",
    },
  },
  coaches: {
    title: "For Coaches",
    subtitle: "Turn your observations into evidence",
    desc: "Focus on coaching while NextTackle handles the admin. Log sessions in under 2 minutes, generate reports automatically, and make development decisions backed by data.",
    icon: TrendingUp,
    benefits: [
      "Session logging in under 2 minutes",
      "Player performance tracking and trend analysis",
      "Individual development plans with data support",
      "Quick drill analytics to measure training effectiveness",
      "Instant attendance tracking via QR code or mobile",
      "Auto-generated player reports for parent meetings",
    ],
    testimonial: {
      quote:
        "I used to spend every Sunday writing up session notes and emails to parents. Now NextTackle does it automatically. I get that time back to actually improve my coaching.",
      name: "Sophie Turner",
      role: "Head Coach, Champion Youth FC",
    },
  },
  analysts: {
    title: "For Performance Analysts",
    subtitle: "Deep-dive analytics at your fingertips",
    desc: "Access granular performance data, create custom KPIs, and build analysis workflows that give your coaching team a competitive edge.",
    icon: BarChart3,
    benefits: [
      "Custom KPI builder and metric tracking",
      "Trend analysis across seasons and squads",
      "Video tagging integrated with performance data",
      "Export raw data for advanced analysis",
      "Comparison tools for squad selection",
      "Performance heatmaps and pattern detection",
    ],
    testimonial: {
      quote:
        "As an analyst, I need flexibility and depth. NextTackle gives me the granular data I need while still being accessible enough for every coach on the staff to use daily.",
      name: "Dr. Ravi Patel",
      role: "Performance Analyst, Elite Sports Academy",
    },
  },
  parents: {
    title: "For Parents",
    subtitle: "Stay close to your child's football journey",
    desc: "Through the Parent Portal, get complete visibility into your child's development — attendance history, performance trends, and coach feedback — all in one place.",
    icon: Users,
    benefits: [
      "Real-time attendance notifications",
      "Monthly performance progress reports",
      "Coach feedback and development notes",
      "Upcoming training schedule and fixtures",
      "Direct messaging with coaches",
      "Goal tracking for individual players",
    ],
    testimonial: {
      quote:
        "As a parent I never really knew how my son was doing in training. With NextTackle, I get weekly updates and can see exactly where he's improving. It's brilliant.",
      name: "Claire Matthews",
      role: "Parent, NextGen Sports Academy",
    },
  },
};

export default function SolutionsPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  return (
    <div className="min-h-screen">
      <div className="bg-gray-900 pt-32 pb-20" data-testid="solutions-page-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-widest text-forest/70 font-bold mb-4">Solutions</p>
          <h1
            className="text-5xl md:text-6xl font-black text-white mb-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Built for <span className="text-forest/70">Everyone</span> in Football
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Whether you're an academy owner, a coach, an analyst, or a parent — NextTackle has the
            right tools for you.
          </p>
        </div>
      </div>

      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="academy-owners" data-testid="solutions-tabs">
            <TabsList className="w-full max-w-2xl mx-auto mb-16 grid grid-cols-2 md:grid-cols-4 h-auto p-1 gap-1">
              <TabsTrigger value="academy-owners" className="py-2.5 text-xs sm:text-sm" data-testid="tab-academy-owners">
                Academy Owners
              </TabsTrigger>
              <TabsTrigger value="coaches" className="py-2.5 text-xs sm:text-sm" data-testid="tab-coaches">
                Coaches
              </TabsTrigger>
              <TabsTrigger value="analysts" className="py-2.5 text-xs sm:text-sm" data-testid="tab-analysts">
                Analysts
              </TabsTrigger>
              <TabsTrigger value="parents" className="py-2.5 text-xs sm:text-sm" data-testid="tab-parents">
                Parents
              </TabsTrigger>
            </TabsList>

            {Object.entries(solutions).map(([key, sol]) => (
              <TabsContent key={key} value={key} data-testid={`solution-content-${key}`}>
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div>
                    <div className="inline-flex items-center gap-2 mb-4">
                      <sol.icon className="w-5 h-5 text-forest" />
                      <span
                        className="text-sm font-bold text-forest uppercase tracking-wider"
                      >
                        {sol.title}
                      </span>
                    </div>
                    <h2
                      className="text-4xl font-black text-gray-900 mb-4"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {sol.subtitle}
                    </h2>
                    <p className="text-gray-600 mb-8 leading-relaxed">{sol.desc}</p>

                    <ul className="space-y-3 mb-8">
                      {sol.benefits.map((benefit) => (
                        <li key={benefit} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-forest flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        onClick={() => setAuthOpen(true)}
                        className="bg-forest hover:bg-forest/90 text-white rounded-sm"
                        data-testid={`solution-trial-${key}`}
                      >
                        Start Free Trial <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => setDemoOpen(true)}
                        variant="outline"
                        className="rounded-sm border-gray-300"
                        data-testid={`solution-demo-${key}`}
                      >
                        Request Demo
                      </Button>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-8 rounded-sm border border-gray-200">
                    <p className="text-lg text-gray-700 italic mb-6 leading-relaxed">
                      "{sol.testimonial.quote}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-forest rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                        {sol.testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-bold text-gray-900 text-sm">
                          {sol.testimonial.name}
                        </div>
                        <div className="text-gray-500 text-xs">{sol.testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>

      <div className="py-20 bg-gray-900 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2
            className="text-4xl font-black text-white mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Not sure which plan fits?
          </h2>
          <p className="text-gray-400 mb-8">
            Book a 30-minute demo and we'll show you exactly how NextTackle works for your role.
          </p>
          <Button
            onClick={() => setDemoOpen(true)}
            className="bg-forest hover:bg-forest/90 text-white rounded-sm px-8 py-3 h-auto"
            data-testid="solutions-demo-btn"
          >
            Book a Demo Call
          </Button>
        </div>
      </div>

      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultTab="register" />
    </div>
  );
}
